/* Order List, Vue Component */
<template>

  <v-container>

    <v-card class="entity-view">
      <v-card-title>Book-in Against Order</v-card-title>

      <v-card-text class="d-flex align-center justify-space-between flex-wrap pb-0">

        <div class="fields d-flex align-center flex-wrap pb-0">
          <div>
            <h4>Reference</h4>
            <output>
              <router-link v-if="entity.Id" class="font-weight-medium text-decoration-none"
                :to="{ name: 'order-view', params: { id: entity.Id } }">
                {{ entity.Reference }}
              </router-link></output>
          </div>

          <div>
            <h4>Status</h4>
            <output><order-status-chip v-if="entity.StatusId_Name"
                :status="entity.StatusId_Name"></order-status-chip></output>
          </div>

        </div>

        <div class="fields d-flex flex-wrap pb-0">

          <div>
            <h4>Member</h4>
            <output>
              <router-link v-if="entity.MemberId" class="font-weight-medium text-decoration-none"
                :to="{ name: 'member-view', params: { id: entity.MemberId } }">
                {{ entity.MemberId_Reference }}
              </router-link></output>
          </div>

          <div>
            <h4>Name</h4>
            <output>{{ entity.MemberId_FullName }}</output>
          </div>

        </div>

        <div class="fields d-flex flex-wrap pb-0">

          <div>
            <h4>Product</h4>
            <output>{{ entity.OrderType }}</output>
          </div>

          <div v-if="entity.ProductId">
            <h4>Product Dims</h4>
            <output>{{ entity.ProductId_Length }}x{{ entity.ProductId_Width }}x{{ entity.ProductId_Height }} mm</output>
          </div>

          <div v-if="entity.CustomLength">
            <h4 v-if="entity.ProductId">Bespoke Dims</h4>
            <h4 v-else-if="entity.QuotePrice">Quote Dims</h4>
            <h4 v-else>Dimensions</h4>
            <output>{{ entity.CustomLength }}x{{ entity.CustomWidth }}x{{ entity.CustomHeight }} mm</output>
          </div>

          <div>
            <h4>Item Description</h4>
            <output>{{ entity.ItemDescription }}</output>
          </div>

        </div>

        <div class="fields d-flex flex-wrap pb-0">

          <div>
            <h4>Retailer</h4>
            <output>{{ entity.ClickCollect ? entity.ClickCollectRetailerId_Name : entity.RetailerName }}</output>
          </div>

          <!-- the customer's reference with the retailer - "Reference" on its own is the ship2me
               reference shown at the top of this screen -->
          <div>
            <h4>Retailer Reference</h4>
            <output>{{ entity.RetailerReference }}</output>
          </div>

          <div v-if="!entity.ClickCollect">
            <h4>Tracking Reference</h4>
            <output>{{ entity.S10Tracking }}</output>
          </div>

          <div v-if="!entity.ClickCollect">
            <h4>Expected Date</h4>
            <output>{{ Display.DisplayDate(entity.RetailerExpectedDeliveryDate) }}</output>
          </div>

          <!-- BCR-119: what the driver collected against -->
          <div v-if="entity.ClickCollect">
            <h4>Collection Date</h4>
            <output>{{ Display.DisplayDate(entity.CollectionDate) }}</output>
          </div>

          <div v-if="entity.Pin">
            <h4>PIN</h4>
            <output>{{ entity.Pin }}</output>
          </div>

        </div>

        <div class="d-flex flex-wrap" v-if="entity.ExtrasNames && entity.ExtrasNames.length > 0">
          <span class="mr-2 mb-1 v-chip theme--light v-size--x-small warning"
            v-for="(extra, extraIndex) in entity.ExtrasNames" :key="extraIndex"><span class="v-chip__content">
              {{ extra }}</span></span>
        </div>

        <div class="my-6 ml-auto mjustify-right">

          <v-btn color="error" x-small :to="{ name: 'order-bookin' }">Not This Order</v-btn>
        </div>

      </v-card-text>

    </v-card>

    <v-container
      v-if="entity && (entity.StatusId_Name == 'Quote' || entity.StatusId_Name == 'Pending' || entity.StatusId_Name == 'Active')"
      class="pa-0 entity-view">
      <v-row>
        <v-col cols="12" sm="12">
          <v-card class="entity-view mt-6 fill-height d-flex flex-column">
            <v-card-title>Details</v-card-title>

            <!-- BCR-156 Stage 4: quote-originated order - show the quote pricing + comments/remarks read-only -->
            <v-card-text v-if="entity.QuoteId && quote" class="pb-0">
              <v-alert type="info" text dense class="mb-0">
                <div v-if="quote.QuotedCost != null"><strong>Base quoted cost:</strong> {{ Display.Money(quote.QuotedCost) }}</div>
                <template v-if="entity.QuoteBreakdownItems && entity.QuoteBreakdownItems.length > 1">
                  <div v-for="(item, itemIndex) in entity.QuoteBreakdownItems.filter(x => x.Id != 'base')" :key="itemIndex">
                    <strong>{{ item.Key }}:</strong> {{ Display.Money(item.Value) }}
                  </div>
                </template>
                <div v-if="quoteTotal != null"><strong>Total charged to customer:</strong> {{ Display.Money(quoteTotal) }}</div>
                <div v-if="quote.Comments"><strong>Customer comments:</strong> {{ quote.Comments }}</div>
                <div v-if="quote.Remarks"><strong>Admin remarks:</strong> {{ quote.Remarks }}</div>
              </v-alert>

              <!-- if the delivered package matches the quote, accept and book at the quoted price -->
              <div v-if="entity.QuotePrice" class="mt-4">
                <v-btn color="success" outlined :disabled="loading" @click="bookItems('accept')">
                  Package matches quote
                </v-btn>
              </div>
            </v-card-text>

            <v-card-text class="pb-0">

              <v-form ref="addForm">

                <!-- Quote order: choose how to price the item that arrived - match it to a product,
                     or measure the actual packages. Pick one. (BCR-156 Stage 4) -->
                <template v-if="entity.QuotePrice">
                  <p class="text-body-2 text--secondary mb-4 mt-4">
                    If the delivered package differs from the quote, re-price it using one of the options below.
                  </p>

                  <v-tabs v-model="quoteTab" class="mb-0">
                    <v-tab>Select a product</v-tab>
                    <v-tab>Volumetric Pricing</v-tab>
                  </v-tabs>
                  <v-divider></v-divider>

                  <v-tabs-items v-model="quoteTab">

                    <!-- Match to a product -->
                    <v-tab-item class="pt-5 px-1">
                      <v-select :items="guesstimateItems" label="Product" v-model="selectedProduct"
                        item-text="text" item-value="value" outlined dense hide-details="auto"
                        @change="onProductSelect"></v-select>
                      <v-row class="mt-1">
                        <v-col cols="4">
                          <v-text-field dense outlined label="Length" type="number" suffix="mm" v-model="newItem.Length"
                            :rules="[validators.requiredInteger, validators.positiveIntegerValidator, validators.integerRange(newItem.Length, 0, 10000)]"
                            @input="onDimensionInput"></v-text-field>
                        </v-col>
                        <v-col cols="4">
                          <v-text-field dense outlined label="Width" type="number" suffix="mm" v-model="newItem.Width"
                            :rules="[validators.requiredInteger, validators.positiveIntegerValidator, validators.integerRange(newItem.Length, 0, 10000)]"
                            @input="onDimensionInput"></v-text-field>
                        </v-col>
                        <v-col cols="4">
                          <v-text-field dense outlined label="Height" type="number" suffix="mm" v-model="newItem.Height"
                            :rules="[validators.requiredInteger, validators.positiveIntegerValidator, validators.integerRange(newItem.Length, 0, 10000)]"
                            @input="onDimensionInput"></v-text-field>
                        </v-col>
                      </v-row>
                    </v-tab-item>

                    <!-- Measure the actual packages (volumetric re-price) -->
                    <v-tab-item class="pt-5 pb-6 px-1">
                      <div class="d-flex align-center mb-3">
                        <span class="text-body-2 text--secondary">Enter each measured package to re-price this item.</span>
                        <v-spacer></v-spacer>
                        <v-btn small outlined color="primary" class="mr-2" @click="addClientDimsBox">
                          + Client Dims
                        </v-btn>
                        <v-btn small text color="primary" @click="addBox">
                          <v-icon left small>{{ icons.mdiPlus }}</v-icon>Add package
                        </v-btn>
                      </div>

                      <v-row v-for="(box, i) in boxes" :key="i" dense class="align-center">
                        <v-col cols="3">
                          <v-text-field dense outlined hide-details label="Length" suffix="cm" type="number"
                            v-model.number="box.L" @input="onBoxesChanged"></v-text-field>
                        </v-col>
                        <v-col cols="3">
                          <v-text-field dense outlined hide-details label="Width" suffix="cm" type="number"
                            v-model.number="box.W" @input="onBoxesChanged"></v-text-field>
                        </v-col>
                        <v-col cols="3">
                          <v-text-field dense outlined hide-details label="Height" suffix="cm" type="number"
                            v-model.number="box.H" @input="onBoxesChanged"></v-text-field>
                        </v-col>
                        <v-col cols="2">
                          <v-text-field dense outlined hide-details label="Qty" type="number"
                            v-model.number="box.Qty" @input="onBoxesChanged"></v-text-field>
                        </v-col>
                        <v-col cols="1" class="text-center px-0">
                          <v-btn icon small @click="removeBox(i)"><v-icon small>{{ icons.mdiClose }}</v-icon></v-btn>
                        </v-col>
                      </v-row>

                      <p v-if="boxes.length === 0" class="text-caption text--secondary mb-0">
                        Add the measured packages to re-price, or use “Package matches quote” above to keep the quoted price.
                      </p>

                      <div class="d-flex align-center mt-3" v-if="boxes.length > 0">
                        <v-btn small outlined color="primary" :loading="volumetricLoading"
                          @click="recalculate">Recalculate</v-btn>
                        <span v-if="volumetric && volumetric.HasDimensions" class="ml-4 text-body-2">
                          Price to use: <strong>{{ Display.Money(volumetricPriceToUse) }}</strong>
                        </span>
                        <span v-else class="ml-4 text-caption warning--text">
                          Recalculate to price these packages before booking.
                        </span>
                      </div>

                      <v-expand-transition>
                        <div v-if="volumetric && volumetric.HasDimensions" class="mt-3">
                          <v-simple-table dense>
                            <tbody>
                              <tr><td class="text--secondary">Volume</td><td class="text-right">{{ volumetric.Volume }} cm³</td></tr>
                              <tr><td class="text--secondary">Volume price</td><td class="text-right">{{ Display.Money(volumetric.VolumePrice) }}</td></tr>
                              <tr><td class="text--secondary">Fuel surcharge ({{ volumetric.FuelSurchargePercent }}%)</td><td class="text-right">{{ Display.Money(volumetric.FuelSurchargeAmount) }}</td></tr>
                              <tr><td class="text--secondary">Base</td><td class="text-right">{{ Display.Money(volumetric.Base) }}</td></tr>
                              <tr><td class="text--secondary">Margin ({{ volumetric.MarginPercent }}%)</td><td class="text-right">{{ Display.Money(volumetric.MarginAmount) }}</td></tr>
                              <tr><td class="text--secondary">Shipping (rounded up to 1p)</td><td class="text-right">{{ Display.Money(volumetric.Total) }}</td></tr>
                              <tr v-if="volumetricSurcharge > 0"><td class="text--secondary">Surcharges (Heavy / DGN / Packaging / HIAB / extras)</td><td class="text-right">{{ Display.Money(volumetricSurcharge) }}</td></tr>
                              <tr class="font-weight-bold"><td>Price to use</td><td class="text-right">{{ Display.Money(volumetricPriceToUse) }}</td></tr>
                            </tbody>
                          </v-simple-table>
                          <p v-if="amountOwedMore != null && amountOwedMore > 0"
                            class="text-caption warning--text mt-2 mb-0">
                            Customer paid {{ Display.Money(quoteTotal) }} — they'll be asked to pay
                            {{ Display.Money(amountOwedMore) }} more.
                          </p>
                        </div>
                      </v-expand-transition>
                    </v-tab-item>
                  </v-tabs-items>
                </template>

                <!-- Standard (non-quote) order: product + dimensions -->
                <template v-else>
                  <v-row>
                    <v-col>
                      <v-select :items="guesstimateItems" label="Select Product" v-model="selectedProduct"
                        item-text="text" item-value="value" @change="onProductSelect"></v-select>
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col>
                      <v-text-field dense outlined flat label="Length" type="number" suffix="mm" v-model="newItem.Length"
                        :rules="[validators.requiredInteger, validators.positiveIntegerValidator, validators.integerRange(newItem.Length, 0, 10000)]"
                        @input="onDimensionInput"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field dense outlined flat label="Width" type="number" suffix="mm" v-model="newItem.Width"
                        :rules="[validators.requiredInteger, validators.positiveIntegerValidator, validators.integerRange(newItem.Length, 0, 10000)]"
                        @input="onDimensionInput"></v-text-field>
                    </v-col>
                    <v-col>
                      <v-text-field dense outlined flat label="Height" type="number" suffix="mm" v-model="newItem.Height"
                        :rules="[validators.requiredInteger, validators.positiveIntegerValidator, validators.integerRange(newItem.Length, 0, 10000)]"
                        @input="onDimensionInput"></v-text-field>
                    </v-col>
                  </v-row>
                </template>

                <v-row>
                  <v-col>
                    <v-text-field hide-details dense outlined flat label="Notes" v-model="newItem.Notes"
                      :rules="[]"></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col v-if="showExtra('Heavy', newItem.Heavy)">
                    <v-checkbox  :disabled="isLocked('Heavy')" hide-details v-model="newItem.Heavy"
                      :color="(newItem.Heavy ? 'info' : 'secondary')">
<template #label>Heavy <v-chip v-if="isAdminOnlyExtra('Heavy')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                  </v-col>
                  <v-col v-if="showExtra('Dangerous Goods Handling', newItem.Dangerous)">
                    <v-checkbox  :disabled="isLocked('Dangerous Goods Handling')" hide-details v-model="newItem.Dangerous"
                      :color="(newItem.Dangerous ? 'warning' : 'secondary')">
<template #label>DGN <v-chip v-if="isAdminOnlyExtra('Dangerous Goods Handling')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                  </v-col>
                  <v-col v-if="showExtra('Packaging', newItem.Packaging)">
                    <v-checkbox  hide-details v-model="newItem.Packaging"
                      :disabled="isLocked('Packaging')"
                      :color="(newItem.Dangerous ? 'warning' : 'secondary')">
<template #label>Packaging? <v-chip v-if="isAdminOnlyExtra('Packaging')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                  </v-col>
                  <v-col v-if="showExtra('HIAB Charge', newItem.HIAB)">
                    <v-checkbox  :disabled="isLocked('HIAB Charge')" hide-details v-model="newItem.HIAB"
                      :color="(newItem.HIAB ? 'warning' : 'secondary')">
<template #label>HIAB Charge <v-chip v-if="isAdminOnlyExtra('HIAB Charge')" x-small color="warning" class="ml-2">Admin only</v-chip></template>
</v-checkbox>
                  </v-col>
                  <!-- BCR-119 AB-07: the collection fee is charged once per collection. A first
                       order always carries it and the admin cannot remove it - the server attaches
                       it at book-in regardless. A duplicate, created because extra items or boxes
                       turned up, skips it unless the admin says this one needed its own collection. -->
                  <v-col v-if="entity.ClickCollect && !entity.OriginalOrderId">
                    <v-checkbox label="Collection Charge" hide-details disabled input-value="true"
                      color="info"></v-checkbox>
                  </v-col>
                  <v-col v-if="entity.ClickCollect && entity.OriginalOrderId">
                    <v-checkbox label="Apply collection fee" hide-details
                      v-model="newItem.ApplyCollectionFee"
                      :color="(newItem.ApplyCollectionFee ? 'warning' : 'secondary')"></v-checkbox>
                  </v-col>
                </v-row>
                <!-- Every other extra (customer-selectable and admin-only alike). Heavy / DGN / Packaging /
                     HIAB are the dedicated checkboxes above. Once the customer has paid, the extras they
                     paid for can't be removed, but others can still be ticked (any extra cost is then a
                     further-payment request); admin-only extras not on the paid invoice can be added or
                     removed freely. -->
                <div v-if="extraOptions.length > 0">
                  <div class="text-caption text--secondary mb-1">Extras</div>
                  <v-row dense>
                    <v-col cols="12" sm="6" md="4" v-for="extra in extraOptions" :key="extra.Id">
                      <v-checkbox hide-details dense v-model="extraIds" :value="extra.Id"
                        :disabled="isExtraLocked(extra)"
                        :color="(extra.AdminOnly ? 'warning' : 'info')">
                        <template #label>
                          {{ extra.Name }} ({{ Display.Money(extra.Price) }})
                          <v-chip v-if="extra.AdminOnly" x-small color="warning" class="ml-2">Admin only</v-chip>
                        </template>
                      </v-checkbox>
                    </v-col>
                  </v-row>
                </div>
                <!-- one hint block below ALL the charge checkboxes, so the notes never split them up -->
                <div v-if="entity.InvoicePaid || entity.QuotePrice" class="text-caption text--secondary mt-2 mb-4">
                  <div v-if="quotePaidLocked">Quote paid: extras are locked until you re-price above (select a product or price measured packages).</div>
                  <div v-else-if="entity.InvoicePaid">Customer has paid: the extras they paid for can't be removed, and ticking another adds a charge.</div>
                </div>
                <v-row>
                  <v-col>
                    <v-checkbox label="Damaged" hide-details v-model="newItem.Damaged"
                      :color="(newItem.Damaged ? 'error' : 'secondary')"></v-checkbox>
                  </v-col>
                  <v-col>
                    <v-checkbox label="Refused Delivery" hide-details v-model="newItem.RefusedDelivery"
                      :color="(newItem.RefusedDelivery ? 'error' : 'secondary')"></v-checkbox>
                  </v-col>
                  <v-col>
                    <v-checkbox label="Do Not Send" hide-details v-model="newItem.DoNotSend"
                      :color="(newItem.DoNotSend ? 'error' : 'secondary')"></v-checkbox>
                  </v-col>
                </v-row>
                                <v-row v-if="!entity.QuotePrice && (entity.ProductId_TwoPerson || newItem.Heavy)">
                  <v-col cols="6">
                    <v-text-field dense outlined flat label="No. of Boxes" type="number" v-model.number="newItem.BoxCount"
                      :rules="[validators.requiredInteger, validators.positiveIntegerValidator]"></v-text-field>
                  </v-col>
                  <v-col cols="6">
                    <v-text-field dense outlined flat label="Weight" type="number" prefix="KG"
                      v-model.number="newItem.BoxWeight"
                      :rules="[validators.requiredInteger, validators.positiveIntegerValidator]"></v-text-field>
                  </v-col>
                </v-row>
                <v-row class="mt-auto d-flex align-center pt-6">
                  <v-col>
                    <v-btn color="secondary" @click="oversized" v-if="!loading">Oversized</v-btn>
                  </v-col>
                  <v-col class="text-right">
                    <!-- quote order: book with the active tab's re-price ("Package matches quote" sits by the quote box above) -->
                    <v-btn v-if="entity.QuotePrice" color="primary" :disabled="!canBookQuote"
                      @click="bookItems()">Book Item</v-btn>
                    <!-- standard order -->
                    <v-btn v-else color="primary" :disabled="!(items.length == 0 && selectedProduct >= 0)"
                      @click="bookItems()">Book Item</v-btn>
                  </v-col>

                </v-row>
              </v-form>

            </v-card-text>

          </v-card>

        </v-col>
      </v-row>
    </v-container>

    <v-chip v-else color="error" large class="my-6 mx-3">Order Status '{{ entity.StatusId_Name }}' prevents item from
      being booked.</v-chip>


  </v-container>

</template>

<script>
import store from '@/store'
import { useRouter } from '@core/utils'
import * as Display from '@/datahelpers/displayTypes'
import OrderStatusChip from '@/views/orderstatus/OrderStatusChip'
import * as FrameworkValidators from '@core/utils/validation'
import * as Validators from '@/datahelpers/validation'

import {
  mdiPencilOutline,
  mdiPlus,
  mdiViewList,
  mdiCheckboxBlankOutline,
  mdiCheckboxMarkedOutline,
  mdiClose
} from '@mdi/js'

import { ref, watch, computed } from '@vue/composition-api'
import items from '@/router/items'

export default {
  components: {
    OrderStatusChip
  },
  setup() {

    const { route, router } = useRouter();

    const entity = computed(() => store.state.app.Order);
    const newItem = ref({
      Heavy: false,
      Dangerous: false,
      Packaging: false,
      HIAB: false,
      ApplyCollectionFee: false,
      BoxCount: 1,
      BoxWeight: 25
    });
    const loading = ref(false);
    const quote = ref(null); // linked quote (for quote-originated orders), fetched on load
    // total charged to the customer for the quote, from the itemised breakdown (base + extras) if we
    // have one - works for both product- and volumetric-priced quotes, unlike entity.QuotePrice, which
    // is only ever set for volumetric quotes (product-priced quotes use ProductId instead and leave
    // QuotePrice null). Falls back to entity.QuotePrice for orders converted before the breakdown existed.
    const quoteTotal = computed(() => {
      const items = entity.value && entity.value.QuoteBreakdownItems;
      if (items && items.length > 0) {
        return Display.RoundMoney(items.reduce((sum, item) => sum + (item.Value || 0), 0));
      }
      return (entity.value && entity.value.QuotePrice) || null;
    });
    const quoteTab = ref(0); // 0 = Select product, 1 = Actual packages (which pricing route the admin picked)
    // BCR-156 Stage 4: actual measured packages (cm) used to re-price a quote-originated order at book-in
    const boxes = ref([]);
    const volumetric = ref(null); // server-computed breakdown for the current boxes
    const volumetricLoading = ref(false);

    const items = ref([]);

    // Extras list: every enabled extra except the four with their own checkboxes above (Heavy / DGN are
    // item flags; Packaging / HIAB feed the price preview). extraIds is the admin's current selection.
    const DEDICATED_EXTRAS = ['Heavy', 'Dangerous Goods Handling', 'Packaging', 'HIAB Charge'];
    const allExtras = ref([]);
    const extraIds = ref([]);
    const extrasLoaded = ref(false);
    const extraOptions = computed(() => allExtras.value.filter(e => e.Enabled && !e.IsCollectionFee && !DEDICATED_EXTRAS.includes(e.Name)));
    // a hard-coded checkbox (Heavy / DGN / Packaging / HIAB) is shown only while its extra is enabled, so
    // disabling an extra removes it from this screen. One the order already carries stays visible (so it
    // can be seen and unticked), and nothing is hidden until the catalogue has loaded.
    const showExtra = (name, alreadyOn) => {
      if (!extrasLoaded.value || alreadyOn) return true;
      return allExtras.value.some(e => e.Name === name && e.Enabled);
    };
    // Heavy / DGN / Packaging / HIAB are customer-selectable unless their extra is flagged Admin Only. Once
    // the customer has paid, one they paid for can't be removed here (the API enforces the same); one that
    // isn't set stays tickable, which raises an extra charge. Checked against what the order carried when
    // loaded (ExtrasNames), not the live ticks, so an admin can still untick their own addition. An
    // admin-only extra is locked only when it's on the paid invoice (e.g. HIAB added on a quote and paid).
    const isPaidFor = (extra) => !extra.AdminOnly || (entity.value.PaidExtraIds || []).includes(extra.Id);
    const isLocked = (name) => {
      if (quotePaidLocked.value) return true;
      const extra = allExtras.value.find(e => e.Name === name);
      return !!(entity.value && entity.value.InvoicePaid && extra && isPaidFor(extra)
        && (entity.value.ExtrasNames || []).includes(name));
    };
    // A paid quote order's price is fixed, so every extra is read-only until the admin re-prices (picks a
    // product, or prices measured packages); only then can an extra be added, raising a further payment.
    const isRepricing = computed(() => quoteTab.value === 0
      ? (selectedProduct.value != null && selectedProduct.value >= 0)
      : !!(volumetric.value && volumetric.value.HasDimensions));
    const quotePaidLocked = computed(() => !!(entity.value && entity.value.QuotePrice && entity.value.InvoicePaid) && !isRepricing.value);
    // same rule for a row in the extras list
    const isExtraLocked = (extra) => {
      if (quotePaidLocked.value) return true;
      return !!(entity.value && entity.value.InvoicePaid && isPaidFor(extra)
        && (entity.value.ExtrasIds || []).includes(extra.Id));
    };
    // true when the named extra is flagged Admin Only (drives the badge)
    const isAdminOnlyExtra = (name) => {
      const extra = allExtras.value.find(e => e.Name === name);
      return !!(extra && extra.AdminOnly);
    };

    const fetchExtras = () => {
      store
        .dispatch('app/fetchOrderExtras', {
          search: {},
          // everything, not just enabled - showExtra() needs to know which hard-coded extras are disabled
          filter: {},
          options: { sortBy: ['SortOrder'], sortDesc: [false], itemsPerPage: 200 }
        })
        .then(response => {
          allExtras.value = response.data.items;
          extrasLoaded.value = true;
        })
        .catch(error => console.log(error));
    }
    fetchExtras();

    const fetchData = (id) => {
      store
        .dispatch('app/fetchOrder', {
          id: id
        })
        .then(response => {
          loading.value = false
          // Set Heavy and Dangerous based on ExtrasNames
          const order = store.state.app.Order;
          extraIds.value = (order && order.ExtrasIds) ? order.ExtrasIds.slice() : [];
          if (order && order.ExtrasNames) {
            console.log(order.ExtrasNames);
            if (order.ExtrasNames.includes('Heavy')) {
              newItem.value.Heavy = true;
            }
            if (order.ExtrasNames.includes('Dangerous Goods Handling')) {
              newItem.value.Dangerous = true;
            }
            if (order.ExtrasNames.includes('Packaging')) {
              newItem.value.Packaging = true;
            }
            if (order.ExtrasNames.includes('HIAB Charge')) {
              newItem.value.HIAB = true;
            }
          }
          updateMpcGuesstimateItem();

          // quote order: default the item dimensions to the quoted dimensions so "Accept quote" and
          // "Measure packages" can book without re-typing (picking a product overrides these)
          if (order && order.QuotePrice && order.CustomLength) {
            newItem.value.Length = order.CustomLength;
            newItem.value.Width = order.CustomWidth;
            newItem.value.Height = order.CustomHeight;
          }

          // if this order came from a quote, load it to show its pricing / comments / remarks
          // and pre-fill the packages editor with the quote's boxes (mm -> cm) as a starting point
          if (order && order.QuoteId) {
            store.dispatch('app/fetchQuote', { id: order.QuoteId })
              .then(r => {
                quote.value = r.data;
                prefillBoxesFromQuote(r.data);
              })
              .catch(() => { });
          }
        })
        .catch(error => {
          console.log(error);
          router.push({ name: 'order-bookin' });
        })
    }

    // ---- BCR-156 Stage 4: actual-package pricing at book-in ----
    // quote boxes are stored in mm; the editor works in cm
    const prefillBoxesFromQuote = (q) => {
      let parsed = [];
      try {
        if (q && q.Boxes) parsed = JSON.parse(q.Boxes);
      } catch (e) { parsed = []; }
      if (Array.isArray(parsed) && parsed.length > 0) {
        boxes.value = parsed.map(b => ({
          L: Math.round((b.L || 0) / 10),
          W: Math.round((b.W || 0) / 10),
          H: Math.round((b.H || 0) / 10),
          Qty: b.Qty > 0 ? b.Qty : 1,
        }));
        recalculate();
      }
    }

    const addBox = () => {
      boxes.value.push({ L: null, W: null, H: null, Qty: 1 });
      volumetric.value = null; // require a fresh recalculate
    }

    // add a package pre-filled with the customer's own quoted dimensions (mm -> cm) - lets the admin
    // re-price at the same size but with extras added (Heavy/DGN/Packaging), without re-typing the dims
    const addClientDimsBox = () => {
      if (!quote.value) return;
      boxes.value.push({
        L: quote.value.Length ? Math.round(quote.value.Length / 10) : null,
        W: quote.value.Width ? Math.round(quote.value.Width / 10) : null,
        H: quote.value.Height ? Math.round(quote.value.Height / 10) : null,
        Qty: 1
      });
      volumetric.value = null; // require a fresh recalculate
    }

    const removeBox = (i) => {
      boxes.value.splice(i, 1);
      volumetric.value = null;
    }

    // editing a dimension invalidates the last computed price until Recalculate is pressed
    const onBoxesChanged = () => {
      volumetric.value = null;
    }

    // boxes -> JSON in mm (cm * 10), matching Quote.Boxes / the API's expected shape.
    const boxesToJsonMm = () => {
      const filled = boxes.value
        .filter(b => b.L > 0 && b.W > 0 && b.H > 0)
        .map(b => ({
          L: Math.round(b.L * 10),
          W: Math.round(b.W * 10),
          H: Math.round(b.H * 10),
          Qty: b.Qty > 0 ? b.Qty : 1,
        }));
      return filled.length > 0 ? JSON.stringify(filled) : "";
    }

    const recalculate = () => {
      const json = boxesToJsonMm();
      if (!json) { volumetric.value = null; return; }
      volumetricLoading.value = true;
      store.dispatch('app/previewVolumetric', { boxes: json, isTwoPerson: newItem.value.Heavy })
        .then(data => { volumetric.value = data; })
        .catch(() => { volumetric.value = null; })
        .finally(() => { volumetricLoading.value = false; });
    }

    // opening the "Actual packages" tab prices any pre-filled packages so the total is visible immediately
    watch(quoteTab, (tab) => {
      if (tab === 1 && boxes.value.length > 0 && !volumetric.value) recalculate();
    });

    // when the Heavy (two-person) flag changes, re-fetch the volumetric preview so the flat-rate base
    // is removed/added correctly (the server ignores BespokePriceBase when isTwoPerson is true)
    watch(() => newItem.value.Heavy, () => {
      if (quoteTab.value === 1 && volumetric.value) recalculate();
    });

    const search = ref({});
    const filter = ref({});
    const options = ref({
      sortBy: ['SortOrder'],
      sortDesc: [false],
      // every product, so the size-based match below sees the whole catalogue (as the server does)
      itemsPerPage: 9999
    });

    var count = ref(1);
    var counts = ref([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])

    var products = ref([]);
    const fetchProducts = () => {
      store
        .dispatch('app/fetchProducts', {
          search: search.value,
          options: options.value,
          filter: filter.value,
        })
        .then(response => {
          products.value = response.data.items;
          loading.value = false
          for (let i = 0; i < products.value.length; i++) {
            if (products.value[i].Enabled && products.value[i].Public) {
              guesstimateItems.value.push({
                value: i,
                text: `${products.value[i].Name} (${products.value[i].Code})`,
                callback: function (e) {
                  e.preventDefault()
                  addGuesstimatedItem(products.value[i]);
                  guesstimateItemsIndex.value = i;
                }
              });
            }
          }
          updateMpcGuesstimateItem();
        })
        .catch(error => {
          console.log(error)
        })
    }
    fetchProducts();

    // add/remove the duplicate-order-only MPC option once both the order and the
    // products have loaded, since they load independently and in either order
    const updateMpcGuesstimateItem = () => {
      const mpcIndex = products.value.findIndex(product => product.Code == 'MPC');
      if (mpcIndex === -1) return;

      const mpcProduct = products.value[mpcIndex];
      const existingIndex = guesstimateItems.value.findIndex(item => item.value === mpcIndex && item.text === mpcProduct.Name);

      if (entity.value.OriginalOrderId) {
        if (existingIndex === -1) {
          guesstimateItems.value.unshift({
            value: mpcIndex,
            text: `${mpcProduct.Name}`,
            callback: function (e) {
              e.preventDefault()
              addGuesstimatedItem(mpcProduct);
              guesstimateItemsIndex.value = mpcIndex;
            }
          });
        }
      } else if (existingIndex !== -1) {
        guesstimateItems.value.splice(existingIndex, 1);
      }
    }

    // fetch the user information when params change
    watch(() => route.value.params.id, (id) => fetchData(id));

    // and call now
    fetchData(route.value.params.id);


    const booleanIcons = status => {
      if (status) return { variant: 'success', icon: mdiCheckboxMarkedOutline }
      return { variant: 'secondary', icon: mdiCheckboxBlankOutline }
    }

    const deleteItem = deleteItem => {
      let deleteIndex = items.value.indexOf(deleteItem);
      items.value.splice(deleteIndex - 1, 1);
    }

    const addForm = ref(null);
    const addItem = () => {
      if (addForm.value.validate()) {
        if (!newItem.value.Notes) delete newItem.value['Notes'];
        if (!newItem.value.Heavy) delete newItem.value['Heavy'];
        if (!newItem.value.Dangerous) delete newItem.value['Dangerous'];
        if (!newItem.value.Packaging) delete newItem.value['Packaging'];
        if (!newItem.value.Damaged) delete newItem.value['Damaged'];
        if (!newItem.value.RefusedDelivery) delete newItem.value['RefusedDelivery'];
        if (!newItem.value.DoNotSend) delete newItem.value['DoNotSend'];
        newItem.value.Guesstimated = false;
        newItem.value.Name = selectedProduct.value ? products.value[selectedProduct.value].Name : "Bespoke Item";
        items.value.push(Object.assign({}, newItem.value));
        newItem.value = {};
        // the extras list is for the whole order, not this item, but its checkboxes sit inside addForm -
        // keep the ticks across the reset
        const keptExtraIds = extraIds.value.slice();
        addForm.value.reset();
        extraIds.value = keptExtraIds;
      }
    }

    const oversized = () => {
      loading.value = true;

      store
        .dispatch('app/bookItemsOrderOversized', {
          id: route.value.params.id,
          entity: entity.value
        })
        .then(response => {
          loading.value = false;
          router.push({ name: 'order-bookin' });
        })
        .catch((error) => {
          loading.value = false;
          if (error.error) {
            if (error.message) serverErrorMessage.value = error.message;
            if (error.errors) serverErrors.value = error.errors
          } else {
            console.log(error);
          }
        });
    }

    const acceptQuote = () => {
      loading.value = true;

      store
        .dispatch('app/bookItemsAcceptQuote', {
          id: route.value.params.id,
          entity: entity.value
        })
        .then(response => {
          loading.value = false;
          router.push({ name: 'order-print', params: { id: route.value.params.id }, query: { b: true } });
        })
        .catch((error) => {
          loading.value = false;
          if (error.error) {
            if (error.message) serverErrorMessage.value = error.message;
            if (error.errors) serverErrors.value = error.errors
          } else {
            console.log(error);
          }
        });
    }

    const acceptQuoteHeavy = () => {
      loading.value = true;

      store
        .dispatch('app/bookItemsAcceptQuoteHeavy', {
          id: route.value.params.id,
          entity: entity.value
        })
        .then(response => {
          loading.value = false;
          router.push({ name: 'order-print', params: { id: route.value.params.id }, query: { b: true } });
        })
        .catch((error) => {
          loading.value = false;
          if (error.error) {
            if (error.message) serverErrorMessage.value = error.message;
            if (error.errors) serverErrors.value = error.errors
          } else {
            console.log(error);
          }
        });
    }

    const addGuesstimatedItem = (product) => {
      let totalLength = product.Length;
      let dividedLength = Math.floor(totalLength / count.value)

      for (var i = 0; i < count.value; i++) {
        newItem.value.Length = dividedLength
        newItem.value.Width = product.Width;
        newItem.value.Height = product.Height;
        newItem.value.Name = product.Name;
        if (!newItem.value.Notes) delete newItem.value['Notes'];
        if (!newItem.value.Dangerous) delete newItem.value['Dangerous'];
        if (!newItem.value.Heavy) delete newItem.value['Heavy'];
        if (!newItem.value.Damaged) delete newItem.value['Damaged'];
        if (!newItem.value.RefusedDelivery) delete newItem.value['RefusedDelivery'];
        if (!newItem.value.DoNotSend) delete newItem.value['DoNotSend'];
        newItem.value.Guesstimated = true;
        items.value.push(Object.assign({}, newItem.value));
        newItem.value = {};
      }
      addForm.value.resetValidation();
    }

    const guesstimateItemsIndex = ref(1);
    const guesstimateItems = ref([]);

    const selectedProduct = ref(null);

    const onProductSelect = (index) => {
      if (index !== null && index !== undefined && products.value[index]) {
        const product = products.value[index];
        newItem.value.Length = product.Length;
        newItem.value.Width = product.Width;
        newItem.value.Height = product.Height;
      }
    };

    const onDimensionInput = () => {
      const length = parseInt(newItem.value.Length) || 0;
      const width = parseInt(newItem.value.Width) || 0;
      const height = parseInt(newItem.value.Height) || 0;

      // Find a matching product based on dimensions: the first to fit in SortOrder, MPC last, a rotatable
      // product taking the item turned any way round - the same rule as ProductService.ProductFits /
      // GetSuitableProduct and the website calculator (ship2meweb libs/productFit.js)
      const fits = (product) => {
        const item = [length, width, height];
        const box = [product.Length, product.Width, product.Height];
        if (product.Rotatable) {
          item.sort((a, b) => a - b);
          box.sort((a, b) => a - b);
        }
        return item.every((d, i) => d <= box[i]);
      };
      const indexes = products.value.map((p, i) => i)
        .sort((a, b) => (products.value[a].Code == 'MPC' ? 1 : 0) - (products.value[b].Code == 'MPC' ? 1 : 0));
      for (const i of indexes) {
        const product = products.value[i];
        if (product.Enabled && product.Public && fits(product)) {
          selectedProduct.value = i;
          return;
        }
      }
      // No match found, clear selection
      selectedProduct.value = null;
    };

    const GuesstimateForBespoke = ref("");
    const fetchGuesstimateForBespoke = () => {
      store
        .dispatch('app/fetchSettings', {
          filter: {
            Category: "Items"
          },
        })
        .then(response => {
          if (response.data && response.data.items && response.data.items.length > 0) {
            let guesstimateSetting = response.data.items.find(setting => setting.Name == "GuesstimateForBespoke");
            if (guesstimateSetting) {
              GuesstimateForBespoke.value = guesstimateSetting.Value;
            }
          }
        })
        .catch(error => {
          console.log(error)
        })
    }
    fetchGuesstimateForBespoke();

    const AllowGuesstimate = computed(() => {
      return (entity && (
        (entity.value.ProductId && entity.value.ProductId_Guesstimate) ||
        (entity.value.ProductId == null && ["YES", "TRUE"].includes(GuesstimateForBespoke.value.toUpperCase()))
      ));
    });

    // Heavy / DGN / Packaging surcharge to add to the volumetric shipping price, reacting to the flags
    // below (which default from the order and can be changed). Mirrors what the API bakes into the price.
    const volumetricSurcharge = computed(() => {
      if (!volumetric.value) return 0;
      let s = 0;
      if (newItem.value.Heavy) s += volumetric.value.HeavyPrice || 0;
      if (newItem.value.Dangerous) s += volumetric.value.DangerousPrice || 0;
      if (newItem.value.Packaging) s += volumetric.value.PackagingPrice || 0;
      if (newItem.value.HIAB) s += volumetric.value.HIABPrice || 0;
      // plus whatever else is ticked in the extras list - the API bills every extra on the order
      extraOptions.value.forEach(e => { if (extraIds.value.includes(e.Id)) s += e.Price || 0; });
      return Display.RoundMoney(s);
    });
    const volumetricPriceToUse = computed(() => {
      if (!volumetric.value) return 0;
      return Display.RoundMoney((volumetric.value.Total || 0) + volumetricSurcharge.value);
    });
    // the additional amount the customer will owe, rounded at calculation time so the comparison
    // (> 0) and the displayed figure are both based on the same clean 2dp value
    const amountOwedMore = computed(() => {
      if (quoteTotal.value == null) return null;
      return Display.RoundMoney(volumetricPriceToUse.value - quoteTotal.value);
    });

    // primary "Book Item" button for a quote: product tab needs a product; packages tab needs a price
    const canBookQuote = computed(() => {
      if (loading.value) return false;
      if (quoteTab.value === 0) return selectedProduct.value !== null && selectedProduct.value >= 0;
      return !!(volumetric.value && volumetric.value.HasDimensions);
    });

    // mode (quotes only): 'accept' = keep quoted price, 'product' = re-price to the selected product,
    // 'packages' = volumetric re-price from the measured packages. For a quote the primary button passes
    // no mode, so it's derived from the active tab; the "Accept quote" button passes 'accept'.
    const bookItems = (mode) => {

      const isQuote = !!(entity.value && entity.value.QuotePrice);
      if (isQuote && !mode) mode = quoteTab.value === 0 ? 'product' : 'packages';

      // measuring packages: require a fresh price before booking
      if (mode === 'packages' && boxesToJsonMm() && !volumetric.value) {
        serverErrorMessage.value = "Please press Recalculate to price the measured packages before booking.";
        return;
      }

      // capture the quote pricing signal now - addForm.reset() below clears the product select / box fields
      const bookProductId = (isQuote && mode === 'product' && selectedProduct.value != null
        && selectedProduct.value >= 0 && products.value[selectedProduct.value])
        ? products.value[selectedProduct.value].Id : null;
      const bookBoxes = (isQuote && mode === 'packages') ? boxesToJsonMm() : "";
      // and the extras list: its checkboxes sit inside addForm too, so the reset would empty extraIds
      // and the API would read [] as "remove every extra"
      const bookExtraIds = extraIds.value.filter(id => extraOptions.value.some(e => e.Id === id));

      if (addForm.value.validate()) {
        if (!newItem.value.Notes) delete newItem.value['Notes'];
        if (!newItem.value.Heavy) delete newItem.value['Heavy'];
        if (!newItem.value.Dangerous) delete newItem.value['Dangerous'];
        if (!newItem.value.Damaged) delete newItem.value['Damaged'];
        if (!newItem.value.RefusedDelivery) delete newItem.value['RefusedDelivery'];
        if (!newItem.value.DoNotSend) delete newItem.value['DoNotSend'];
        newItem.value.Guesstimated = false;
        newItem.value.Name = selectedProduct.value ? products.value[selectedProduct.value].Name : "Bespoke Item";
        items.value.push(Object.assign({}, newItem.value));
        newItem.value = {};
        addForm.value.reset();
      }
      else {
        return
      }

      loading.value = true;

      // the extras list rides on the first item (the API reads it from there); only sent once the
      // catalogue has loaded, otherwise the server would treat an empty list as "remove everything".
      // The server restricts it to admin-only extras when the customer has already paid.
      if (extrasLoaded.value && items.value.length > 0) {
        items.value[0].ExtraIds = bookExtraIds;
      }

      // quote-priced orders: attach the pricing signal for the chosen mode (product / packages / accept)
      if (isQuote && items.value.length > 0) {
        if (bookProductId != null) {
          items.value[0].ProductId = bookProductId;
        } else if (bookBoxes) {
          items.value[0].Boxes = bookBoxes;
        }
        // 'accept' => neither, so the API keeps the quoted price
      }

      store
        .dispatch('app/bookItemsOrder', {
          id: route.value.params.id,
          entity: entity.value,
          items: items.value
        })
        .then(response => {
          loading.value = false;
          router.push({ name: 'order-print', params: { id: response.data.Id }, query: { b: true } });
        })
        .catch((error) => {
          loading.value = false;
          if (error.error) {
            if (error.message) serverErrorMessage.value = error.message;
            if (error.errors) serverErrors.value = error.errors
          } else {
            console.log(error);
          }
        });
    }

    const validators = {
      ...FrameworkValidators,
      ...Validators
    };

    return {
      AllowGuesstimate,
      guesstimateItemsIndex,
      guesstimateItems,
      bookItems,
      addForm,
      loading,
      entity,
      newItem,
      extraOptions,
      extraIds,
      isLocked,
      isExtraLocked,
      quotePaidLocked,
      isAdminOnlyExtra,
      showExtra,
      quote,
      quoteTotal,
      quoteTab,
      canBookQuote,
      volumetricSurcharge,
      volumetricPriceToUse,
      amountOwedMore,
      boxes,
      volumetric,
      volumetricLoading,
      addBox,
      addClientDimsBox,
      removeBox,
      onBoxesChanged,
      recalculate,
      Display,
      booleanIcons,
      onProductSelect,
      onDimensionInput,
      selectedProduct,
      items,
      deleteItem,
      addItem,
      oversized,
      acceptQuote,
      acceptQuoteHeavy,
      validators,
      count,
      counts,
      icons: {
        mdiPencilOutline,
        mdiPlus,
        mdiViewList,
        mdiClose
      },
    };
  }
}
</script>

<style lang="scss" scoped>
::v-deep .guesstimate {

  .v-input__slot {
    border-left: 1px solid rgba(94, 86, 105, 0.14);
    border-right: 1px solid rgba(94, 86, 105, 0.14);
  }

  .v-select__selections {
    height: 48px;
    min-width: 310px;

    .v-btn {
      min-height: 48px;
    }
  }
}

::v-deep .guesstimatecount {

  .v-input__slot {
    border-left: 1px solid rgba(94, 86, 105, 0.14);
    border-right: 1px solid rgba(94, 86, 105, 0.14);
  }

  .v-select__selections {
    height: 48px;

    .v-btn {
      min-height: 48px;
    }
  }
}
</style>
